import React from "react";
import Link from "next/link";
import Image from "next/image";
import { ChatBubbleOvalLeftIcon } from "@heroicons/react/24/outline";
import HandDrawnBracket from "../atoms/svgs/HandDrawnBracket";
import BrandColorsDivider from "../atoms/dividers/BrandColors.divider";

interface ImageProps {
	src: string;
	alt: string;
}

interface CTASectionProps {
	images?: ImageProps[];
}

const CTASection = ({ images }: CTASectionProps) => {
	// Split images into two columns
	const allDefaultImages = [
		// Root folder images
		// { src: "/slide-show/Fastlanemockup.png", alt: "Fastlane Mockup" },
		// { src: "/slide-show/LENSQUOTE-MOCKUP 1.png", alt: "Lensquote Mockup" },
		// { src: "/slide-show/allinmockup.png", alt: "All In Mockup" },
		// { src: "/slide-show/gatewaymockup.png", alt: "Gateway Mockup" },
		// { src: "/slide-show/medrepmockup.png", alt: "MedRep Mockup" },
		// { src: "/slide-show/nexmockup.png", alt: "Nexsyis Mockup" },
		// { src: "/slide-show/phone in hand mockup 1.png", alt: "Phone Mockup" },
		// { src: "/slide-show/usafbappmockup.png", alt: "USAFB App Mockup" },
		// { src: "/slide-show/yojomockup.png", alt: "Yojo Mockup" },
		// For Agencies
		{ src: "/slide-show/For Agencies/Crafted1.jpg", alt: "Crafted Project 1" },
		{ src: "/slide-show/For Agencies/Crafted2.jpg", alt: "Crafted Project 2" },
		{ src: "/slide-show/For Agencies/Crafted3.jpg", alt: "Crafted Project 3" },
		{ src: "/slide-show/For Agencies/borshoffMobile.jpg", alt: "Borshoff Mobile" },
		{ src: "/slide-show/For Agencies/boshoffDesktop.jpg", alt: "Borshoff Desktop" },
		{ src: "/slide-show/For Agencies/imiDesktop.jpg", alt: "IMI Desktop" },
		{ src: "/slide-show/For Agencies/imiMobile.jpg", alt: "IMI Mobile" },
		{ src: "/slide-show/For Agencies/ptrDesktop.jpg", alt: "PTR Desktop" },
		{ src: "/slide-show/For Agencies/ptrMobile.jpg", alt: "PTR Mobile" },
		{ src: "/slide-show/For Agencies/zooDesktop.jpg", alt: "Zoo Desktop" },
		{ src: "/slide-show/For Agencies/zooMobile.jpg", alt: "Zoo Mobile" },
		// For Product Teams
		{ src: "/slide-show/For Product Teams/AllIn.jpg", alt: "All In" },
		{ src: "/slide-show/For Product Teams/Fastlane.jpg", alt: "Fastlane" },
		{ src: "/slide-show/For Product Teams/Lensquote.jpg", alt: "Lensquote" },
		{ src: "/slide-show/For Product Teams/MISOGateway.jpg", alt: "MISO Gateway" },
		{ src: "/slide-show/For Product Teams/MISOLMPContourMap.jpg", alt: "MISO LMP Contour Map" },
		{ src: "/slide-show/For Product Teams/MedRep.jpg", alt: "Med Rep" },
		{ src: "/slide-show/For Product Teams/Nexsyis.jpg", alt: "Nexsyis" },
		{ src: "/slide-show/For Product Teams/usafbDashboard.jpg", alt: "USAFB Dashboard" },
		{ src: "/slide-show/For Product Teams/usafbLeagueFinder.jpg", alt: "USAFB League Finder" },
		{ src: "/slide-show/For Product Teams/yojo.jpg", alt: "Yojo" },
	];

	// Shuffle helper function - consistent shuffle for SSR (could be more robust but simple for now)
	// We'll just distribute them roughly evenly for default
	const defaultMid = Math.ceil(allDefaultImages.length / 2);
	const defaultLeftImages = allDefaultImages.slice(0, defaultMid);
	const defaultRightImages = allDefaultImages.slice(defaultMid);

	let leftColumnImages = defaultLeftImages;
	let rightColumnImages = defaultRightImages;

	if (images && images.length > 0) {
		const mid = Math.ceil(images.length / 2);
		leftColumnImages = images.slice(0, mid);
		rightColumnImages = images.slice(mid);
	}

	return (
		<div className="bg-black px-2 xl:px-0">
			<div className="container mx-auto px-4 max-w-[1322px]">
				<div className="py-8 md:py-0 grid grid-cols-1 md:grid-cols-[repeat(16,minmax(0,1fr))] gap-12 md:gap-12  items-center">
					{/* Left Content Block */}
					<div className="flex flex-col md:col-span-7 items-start gap-8 md:py-4">
						<h2 className="font-display font-bold leading-tight text-white">
							Let&apos;s Build Something People Love –{<br />}
							<span>
								<span className="text-blue">
									<HandDrawnBracket
										animateColors={true}
										side="left"
										height="clamp(44px,3.75vw,54px)"
										className="inline-block -ml-[5px] md:-ml-[9px] -mr-2 -mt-2 relative"
									/>
								</span>
								<span className="text-white">Faster Than Ever</span>
								<span className="text-blue">
									<HandDrawnBracket
										animateColors={true}
										side="right"
										height="clamp(44px,3.75vw,54px)"
										className="inline-block -ml-3 -mt-2 relative"
									/>
								</span>
							</span>
						</h2>
						<p className="text-[clamp(20px,2.5vw,36px)] text-white leading-tight">
							Book a no-pressure discovery call. We&apos;ll review your product goals, diagnose
							bottlenecks, and see if we&apos;re the right fit.
						</p>
						<Link href="/contact">
							<a className="bg-blue text-white font-display py-4 px-12 rounded-md hover:bg-blue/80 transition-colors shadow-lg text-lg flex items-center gap-2">
								<ChatBubbleOvalLeftIcon className="w-5 h-5" />
								Talk to Crafted
							</a>
						</Link>
					</div>

					{/* Right Image Grid - Two Vertical Scrolling Columns */}
					<div className="grid grid-cols-1 grid-rows-2 md:grid-cols-2 md:grid-rows-1 md:col-span-9 gap-4 overflow-hidden max-h-[clamp(600px,55.56vw,800px)]">
						{/* Left Column - Scrolling Down */}
						<div className="relative h-full w-full overflow-hidden">
							<div className="inline-flex md:flex flex-row md:flex-col gap-4 animate-scroll md:animate-scroll-down">
								{/* Duplicate images for seamless loop */}
								{[...leftColumnImages, ...leftColumnImages].map((image, index) => (
									<div
										key={index}
										className="relative rounded-lg overflow-hidden aspect-[340/260] bg-gray-800 flex-shrink-0 w-[340px] md:w-full"
									>
										<Image
											src={image.src}
											alt={image.alt}
											layout="fill"
											objectFit="cover"
											className="rounded-lg"
										/>
									</div>
								))}
							</div>
						</div>

						{/* Right Column - Scrolling Up */}
						<div className="relative h-full w-full overflow-hidden flex justify-end md:block">
							<div className="inline-flex md:flex flex-row md:flex-col gap-4 animate-scroll-reverse md:animate-scroll-up">
								{/* Duplicate images for seamless loop */}
								{[...rightColumnImages, ...rightColumnImages].map((image, index) => (
									<div
										key={index}
										className="relative rounded-lg overflow-hidden aspect-[340/260] bg-gray-800 flex-shrink-0 w-[340px] md:w-full"
									>
										<Image
											src={image.src}
											alt={image.alt}
											layout="fill"
											objectFit="cover"
											className="rounded-lg"
										/>
									</div>
								))}
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default CTASection;
